
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useNuvemshopCallback = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processando autorização da Nuvemshop...');
  const [details, setDetails] = useState<string>('');

  const buildVersion = `${new Date().toISOString()}-callback-v8-simplified`;

  useEffect(() => {
    console.log('🎯 NUVEMSHOP CALLBACK HOOK CARREGADO!');
    console.log('🎯 Build version:', buildVersion);
    console.log('🎯 Location:', location);
    console.log('🎯 SearchParams:', Object.fromEntries(searchParams));
    console.log('🎯 URL completa:', window.location.href);
    console.log('🎯 Pathname:', window.location.pathname);
  }, []);

  const processCallback = async () => {
    try {
      console.log('🔄 INICIANDO PROCESSAMENTO DO CALLBACK NUVEMSHOP');
      console.log('📍 URL atual:', window.location.href);
      console.log('📍 Pathname:', window.location.pathname);
      
      // PRIORIDADE 1: Verificar parâmetros de sucesso direto
      const success = searchParams.get('success');
      const store = searchParams.get('store');
      const storeName = searchParams.get('store_name');
      const errorParam = searchParams.get('error');

      console.log('📝 Parâmetros analisados:', { 
        success, 
        store, 
        storeName, 
        error: errorParam,
        hasSuccess: success === 'true',
        hasStore: !!store
      });

      // Se tem parâmetro de erro explícito, mostrar erro
      if (errorParam) {
        console.error('❌ Erro explícito via parâmetro:', errorParam);
        setStatus('error');
        setMessage('Erro na integração');
        setDetails(decodeURIComponent(errorParam));
        return;
      }

      // Se tem parâmetros de sucesso, processar como sucesso IMEDIATAMENTE
      if (success === 'true' && store) {
        console.log('🎉 SUCESSO DETECTADO VIA PARÂMETROS DIRETOS!');
        console.log('✅ Store ID:', store);
        console.log('✅ Store Name:', storeName);
        
        setStatus('success');
        setMessage('Integração configurada com sucesso!');
        setDetails(`Loja ${storeName ? decodeURIComponent(storeName) : store} conectada com sucesso`);

        // Mostrar toast de sucesso
        toast({
          title: "✅ Integração Nuvemshop",
          description: `Loja ${storeName ? decodeURIComponent(storeName) : store} conectada com sucesso!`,
          variant: "default",
        });

        // Redirecionar após 2 segundos
        setTimeout(() => {
          console.log('🔄 Redirecionando para /integrations...');
          navigate('/integrations');
        }, 2000);
        return;
      }

      // Se não tem parâmetros de sucesso, verificar se é callback OAuth tradicional
      console.log('🔍 Não encontrado parâmetros de sucesso, verificando OAuth...');
      
      let code = searchParams.get('code');
      let state = searchParams.get('state');
      let storeId = searchParams.get('store_id');

      console.log('📝 Parâmetros OAuth detectados:', { 
        hasCode: !!code, 
        hasState: !!state, 
        storeId 
      });

      // Verificar se tem os parâmetros mínimos para OAuth
      if (!code || !state) {
        console.log('⚠️ Parâmetros OAuth insuficientes, verificando se é um acesso direto...');
        
        // Se não tem nenhum parâmetro relevante, redirecionar para integrações
        if (!success && !code && !errorParam) {
          console.log('🔄 Nenhum parâmetro relevante encontrado, redirecionando...');
          navigate('/integrations');
          return;
        }

        console.error('❌ Parâmetros de callback inválidos');
        setStatus('error');
        setMessage('Parâmetros de autorização inválidos');
        setDetails('Código de autorização ou state ausente. Tente conectar novamente.');
        return;
      }

      console.log('✅ Parâmetros OAuth válidos encontrados, processando...');
      setMessage('Finalizando configuração da integração...');

      // Processar OAuth tradicional
      console.log('📡 Enviando requisição para Edge Function...');
      const { data, error: functionError } = await supabase.functions.invoke('nuvemshop-integration', {
        body: {
          action: 'oauth_callback',
          code: code,
          state: state,
          store_id: storeId
        }
      });

      console.log('📡 Resposta da Edge Function:', { data, error: functionError });

      if (functionError) {
        console.error('❌ Erro na Edge Function:', functionError);
        setStatus('error');
        setMessage('Erro ao finalizar configuração');
        setDetails(`Erro na comunicação: ${functionError.message || 'Erro interno do servidor'}`);
        return;
      }

      if (!data?.success) {
        console.error('❌ Falha na integração:', data?.error);
        setStatus('error');
        setMessage('Falha ao configurar integração');
        setDetails(data?.error || 'Resposta inválida do servidor');
        return;
      }

      console.log('🎉 OAuth tradicional processado com sucesso!');
      setStatus('success');
      setMessage('Integração configurada com sucesso!');
      setDetails(`Loja ${data.store_name || data.store_id} conectada via OAuth`);

      toast({
        title: "✅ Integração Nuvemshop",
        description: `Loja ${data.store_name || data.store_id} conectada com sucesso!`,
        variant: "default",
      });

      setTimeout(() => {
        navigate('/integrations');
      }, 2000);

    } catch (error) {
      console.error('💥 Erro inesperado no callback:', error);
      setStatus('error');
      setMessage('Erro inesperado');
      
      let errorDetails = 'Erro desconhecido';
      if (error instanceof Error) {
        errorDetails = error.message;
        if (error.message.includes('fetch')) {
          errorDetails = 'Erro de conectividade. Verifique sua conexão com a internet.';
        } else if (error.message.includes('JSON')) {
          errorDetails = 'Erro no formato da resposta do servidor.';
        }
      }
      
      setDetails(errorDetails);
    }
  };

  useEffect(() => {
    // Delay pequeno para garantir que todos os parâmetros estão disponíveis
    const timer = setTimeout(() => {
      processCallback();
    }, 100);

    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  const handleRetry = () => {
    console.log('🔄 Tentativa de retry iniciada');
    setStatus('processing');
    setMessage('Processando autorização da Nuvemshop...');
    setDetails('');
    // Recarregar a página para tentar novamente
    window.location.reload();
  };

  const handleGoBack = () => {
    console.log('🔙 Retornando para integrações');
    navigate('/integrations');
  };

  return {
    status,
    message,
    details,
    buildVersion,
    handleRetry,
    handleGoBack
  };
};
